type SchemaField = {
  type?: string | string[] | null;
  format?: string;
  enum?: string[];
  nullable?: boolean;
  properties?: Record<string, SchemaField>;
  items?: any;
  allOf?: any;
  required?: string[];
};

export function flattenStringFields(
  schema: Record<string, any>,
  parentKey = ""
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(schema)) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    const type = value?.type;

    // Handle nested object with properties
    if ((type === "object" || type?.includes?.("object")) && value.properties) {
      const nested = flattenStringFields(value.properties, fullKey);
      Object.assign(result, nested);
      continue;
    }

    // Handle primitive types
    let finalType: string | undefined;

    if (Array.isArray(type)) {
      if (type.includes("string") && type.includes("null")) {
        finalType = key;
      } else if (type.includes("string")) {
        finalType = key;
      } else if (type.includes("number") && type.includes("null")) {
        finalType = "number";
      } else if (type.includes("number")) {
        finalType = "number";
      }
    } else if (type === "string") {
      finalType = value.nullable ? "string | null" : "string";
    } else if (type === "number") {
      finalType = value.nullable ? "number | null" : "number";
    }

    if (finalType) {
      result[fullKey] = finalType;
    }
  }

  return result;
}

function resolveEnum(def, components) {
  if (def.allOf) {
    for (const sub of def.allOf) {
      if (sub["$ref"]) {
        const ref = sub["$ref"];
        if (ref.startsWith("#/components/schemas/")) {
          const componentName = ref.replace("#/components/schemas/", "");
          const component = components[componentName];
          if (component && component.enum && component.enum.length) {
            return `${component.enum[0]}`; // return the first enum value as a string
          }
        }
      }
      if (sub.enum && sub.enum.length) {
        return `${sub.enum[0]}`; // return the first enum value as a string
      }
    }
  }
  if (def.enum && def.enum.length) {
    return `${def.enum[0]}`;
  }
  return null;
}

function resolveType(def, key, debug, components) {
  if (!def) return "unknown";

  const enumValue = resolveEnum(def, components);
  if (enumValue) return enumValue;

  const nullable =
    def.nullable || (Array.isArray(def.type) && def.type.includes("null"));

  let baseType;
  if (Array.isArray(def.type)) {
    baseType = def.type.find((t) => t !== "null");
  } else {
    baseType = def.type;
  }
  switch (baseType) {
    case "string":
      return nullable ? key : `string`;
    case "number":
      return nullable ? 0 : `'number'`;
    case "boolean":
      return nullable ? `boolean | null` : `boolean`;
    case "object":
      return "object";
    case "array":
      return "array";
    default:
      return nullable ? "unknown | null" : "unknown";
  }
}

export function convertSchema(schema, components, debug) {
  const parse = (key, def) => {
    if (!def) return "unknown";

    // allOf with enum shortcut
    const enumValue = resolveEnum(def, components);
    if (enumValue) return enumValue;

    if (def["$ref"]) {
      const ref = def["$ref"];
      if (ref.startsWith("#/components/schemas/")) {
        const componentName = ref.replace("#/components/schemas/", "");
        const component = components[componentName];

        return parse(key, component);
      }
    }

    if (def.allOf) {
      for (const sub of def.allOf) {
        if (sub["$ref"]) {
          const ref = sub["$ref"];
          if (ref.startsWith("#/components/schemas/")) {
            const componentName = ref.replace("#/components/schemas/", "");
            const component = components[componentName];

            return parse(key, component);
          }
        }
        if (typeof sub === "object") {
          return parse(key, sub);
        }
      }
    }

    const nullable =
      def.nullable || (Array.isArray(def.type) && def.type.includes("null"));

    // Handle specific cases based on type
    if (def.type === "array") {
      if (def.items["$ref"]) {
        const ref = def.items["$ref"];
        if (ref.startsWith("#/components/schemas/")) {
          const componentName = ref.replace("#/components/schemas/", "");
          const component = components[componentName];
          return [parse(key, component)];
        }
      }
      const items = def.items;
      if (items?.type === "object" && items.properties) {
        const obj = {};
        for (const key in items.properties) {
          obj[key] = parse(key, items.properties[key]);
        }
        return [obj];
      } else {
        return [resolveType(items, key, debug, components)];
      }
    }

    if (def.type === "object" && def.properties) {
      const obj = {};
      for (const key in def.properties) {
        obj[key] = parse(key, def.properties[key]);
      }
      return obj;
    }

    if (def.type === "object" && def.examples) {
      return def.examples;
    }

    // Use field key for clear types, 0 for numeric types
    if (def.type === "string") {
      return key;
    }

    if (def.type === "number") {
      return nullable ? `0 | null` : 0;
    }

    if (def.type === "boolean") {
      return nullable ? `boolean | null` : `boolean`;
    }

    return resolveType(def, key, debug, components);
  };

  const output = {};
  for (const key in schema) {
    output[key] = parse(key, schema[key]);
  }

  return output;
}
